import { X, ArrowUpToLine } from "lucide-react";
import { useState } from "react";

export type FilterOptions = "all" | "added" | "not-added";
export type SortOptions = "name" | "quantity";

// ─── Subcomponents ───────────────────────────────────────────────────────────

function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative flex-1 w-fit">
      <input
        type="text"
        placeholder="Search..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 pr-9 bg-main text-secondary border border-secondary/50 rounded-lg outline-none focus:border-secondary transition-colors text-base lg:text-sm"
      />
      {value && (
        <button
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary/50 hover:text-secondary/80 transition-colors"
          type="button"
          aria-label="Clear search"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

function FilterToggleButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 rounded-lg transition-all flex items-center justify-center font-medium ${
        active
          ? "bg-secondary text-main border border-secondary shadow-md"
          : "border border-secondary/50 text-secondary/70 hover:bg-secondary/10 hover:border-secondary/70 hover:text-secondary"
      }`}
      type="button"
      aria-label="Toggle filters"
    >
      <img
        src={active ? "/icons/filter-main.svg" : "/icons/filter.svg"}
        alt="Filter"
        className="w-5 h-5 transition-all"
      />
    </button>
  );
}

function ScrollTopButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="lg:hidden px-3 py-2 rounded-lg border border-secondary/50 text-secondary/70 hover:bg-secondary/10 hover:border-secondary/70 hover:text-secondary transition-all flex items-center justify-center"
      type="button"
      aria-label="Scroll to top"
    >
      <ArrowUpToLine size={20} />
    </button>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-2 py-1 rounded transition-colors ${
        active ? "bg-secondary text-main" : "border border-secondary/50 text-secondary hover:bg-secondary/10"
      }`}
    >
      {children}
    </button>
  );
}

function FilterPanel({
  filterBy,
  onFilterChange,
  sortBy,
  onSortChange,
  open,
}: {
  filterBy: FilterOptions;
  onFilterChange: (f: FilterOptions) => void;
  sortBy: SortOptions;
  onSortChange: (s: SortOptions) => void;
  open: boolean;
}) {
  return (
    <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
      <div className="min-h-0 overflow-hidden">
        <div className="flex gap-2 pt-4 flex-wrap border-secondary/10">
          <div className="flex gap-1 items-center">
            <Chip active={filterBy === "all"} onClick={() => onFilterChange("all")}>All</Chip>
            <Chip active={filterBy === "added"} onClick={() => onFilterChange("added")}>Added</Chip>
            <Chip active={filterBy === "not-added"} onClick={() => onFilterChange("not-added")}>Not Added</Chip>
          </div>

          <div className="flex gap-1 items-center ml-auto">
            <Chip active={sortBy === "name"} onClick={() => onSortChange("name")}>A-Z</Chip>
            <Chip active={sortBy === "quantity"} onClick={() => onSortChange("quantity")}>Qty</Chip>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Public export ────────────────────────────────────────────────────────────

export function Filter({
  value,
  onChange,
  filterBy,
  onFilterChange,
  sortBy,
  onSortChange,
  onScrollTop,
}: {
  value: string;
  onChange: (value: string) => void;
  filterBy: FilterOptions;
  onFilterChange: (filter: FilterOptions) => void;
  sortBy: SortOptions;
  onSortChange: (sort: SortOptions) => void;
  onScrollTop?: () => void;
}) {
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  return (
    <div className=" z-10 lg:bg-transparent w-full px-4 pt-2">
      <div className="flex gap-2">
        <SearchInput value={value} onChange={onChange} />
        <FilterToggleButton active={showFilterPanel} onClick={() => setShowFilterPanel((v) => !v)} />
        {onScrollTop && <ScrollTopButton onClick={onScrollTop} />}
      </div>

      <FilterPanel
        filterBy={filterBy}
        onFilterChange={onFilterChange}
        sortBy={sortBy}
        onSortChange={onSortChange}
        open={showFilterPanel}
      />
    </div>
  );
}
